import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lines, Overline, FadeUp } from "./Reveal";
import { CONTACT } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = ({ t, lang }) => {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/inquiries`, { ...form, company: form.company || null, lang });
      setSent(true);
      toast.success(t.contact.success);
      setForm({ name: "", email: "", company: "", message: "" });
    } catch {
      toast.error(t.contact.error);
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-0 border-b border-ink/20 rounded-none px-0 py-4 font-sans text-base text-ink placeholder:text-ink/35 focus:outline-none focus:border-earth transition-colors duration-500";

  return (
    <footer id="contact" className="pt-28 md:pt-44 bg-cream" data-testid="contact-section">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 grid grid-cols-12 gap-10 md:gap-6 pb-24 md:pb-36">
        <div className="col-span-12 lg:col-span-5">
          <Overline className="mb-6">{t.contact.overline}</Overline>
          <Lines lines={[t.contact.title]} className="font-serif text-5xl md:text-6xl lg:text-[5vw] tracking-tight text-ink" />
          <FadeUp delay={0.25}>
            <div className="mt-12 space-y-4 max-w-md" data-testid="contact-channels">
              <a
                href={CONTACT.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-button"
                className="group flex items-center justify-between gap-6 border border-ink/15 px-6 py-5 hover:border-earth transition-colors duration-500"
              >
                <span className="font-sans text-xs uppercase tracking-[0.25em] text-ink">{t.contact.whatsappLabel}</span>
                <span className="font-sans text-sm text-ink/60 group-hover:text-earth transition-colors duration-500">{CONTACT.phone}</span>
              </a>
              <a
                href={`mailto:${CONTACT.email}`}
                data-testid="contact-email-button"
                className="group flex items-center justify-between gap-6 border border-ink/15 px-6 py-5 hover:border-earth transition-colors duration-500"
              >
                <span className="font-sans text-xs uppercase tracking-[0.25em] text-ink">{t.contact.emailLabel}</span>
                <span className="font-sans text-sm text-ink/60 group-hover:text-earth transition-colors duration-500 break-all">{CONTACT.email}</span>
              </a>
            </div>
            <div className="mt-10 space-y-2">
              <p className="font-sans text-[11px] uppercase tracking-[0.3em] text-ink/40">{t.contact.locations}</p>
            </div>
          </FadeUp>
        </div>
        <FadeUp delay={0.2} className="col-span-12 lg:col-span-6 lg:col-start-7">
          {sent ? (
            <div className="border border-ink/10 p-10 md:p-16 h-full flex flex-col justify-center" data-testid="contact-success">
              <span className="font-serif italic text-earth text-5xl">&ldquo;</span>
              <p className="font-serif text-2xl md:text-3xl text-ink leading-snug mt-2">{t.contact.success}</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-8" data-testid="contact-form">
              <input required value={form.name} onChange={set("name")} placeholder={t.contact.name} className={inputCls} data-testid="contact-input-name" />
              <input required type="email" value={form.email} onChange={set("email")} placeholder={t.contact.email} className={inputCls} data-testid="contact-input-email" />
              <input value={form.company} onChange={set("company")} placeholder={t.contact.company} className={inputCls} data-testid="contact-input-company" />
              <textarea required rows={4} value={form.message} onChange={set("message")} placeholder={t.contact.message} className={`${inputCls} resize-none`} data-testid="contact-input-message" />
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit-button"
                className="font-sans text-xs uppercase tracking-[0.25em] bg-ink text-cream px-10 py-5 hover:bg-earth transition-colors duration-700 disabled:opacity-50"
              >
                {sending ? t.contact.sending : t.contact.submit}
              </button>
            </form>
          )}
        </FadeUp>
      </div>

      <div className="border-t border-ink/10">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-10 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <p className="font-serif text-[16vw] md:text-[10vw] leading-[0.85] tracking-[0.08em] text-ink select-none" data-testid="footer-wordmark">
            ANKLA
          </p>
          <div className="pb-2 space-y-1 md:text-right">
            <p className="font-sans text-[11px] uppercase tracking-[0.25em] text-ink/50 break-all">{CONTACT.email}</p>
            <p className="font-sans text-[11px] uppercase tracking-[0.25em] text-ink/50">{CONTACT.phone}</p>
            <p className="font-sans text-[11px] uppercase tracking-[0.25em] text-ink/50">{t.contact.founder}</p>
            <p className="font-sans text-[11px] uppercase tracking-[0.25em] text-ink/35">© {new Date().getFullYear()} {t.contact.rights}</p>
          </div>
        </div>
      </div>
    </footer>
  );
};
